import api from '../api';
import { RewardItem } from '../../services/engagement';
import type { LeaderboardResponse } from '../../services/engagement';

export type AdminRewardItem = RewardItem & {
  active: boolean;
  stock: number;
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
};

export type AdminRewardsResponse = { items: AdminRewardItem[] };

export async function adminListRewards(params?: { active?: boolean }): Promise<AdminRewardItem[]> {
  const res = await api.get('/admin/engagement/rewards', { params });
  return (res.data as AdminRewardsResponse).items || [];
}

export async function adminCreateReward(body: {
  name: string;
  type: string;
  cost_points: number;
  image_url?: string;
  stock?: number;
  active?: boolean;
  metadata?: Record<string, any>;
}): Promise<AdminRewardItem> {
  const res = await api.post('/admin/engagement/rewards', body);
  return res.data as AdminRewardItem;
}

export async function adminUpdateReward(id: number, body: Partial<{
  name: string;
  type: string;
  cost_points: number;
  image_url: string;
  stock: number;
  active: boolean;
  metadata: Record<string, any>;
}>): Promise<{ ok: boolean }>{
  const res = await api.put(`/admin/engagement/rewards/${id}`, body);
  return res.data as { ok: boolean };
}

export async function adminDeleteReward(id: number): Promise<{ ok: boolean }>{
  const res = await api.delete(`/admin/engagement/rewards/${id}`);
  return res.data as { ok: boolean };
}

export type AdminRedemption = {
  id: number;
  user_id: number;
  reward_id: number;
  status: 'pending'|'approved'|'rejected'|'fulfilled'|string;
  created_at?: string;
};

export type AdminRedemptionsResponse = { items: AdminRedemption[] };

export async function adminListRedemptions(params?: { status?: string }): Promise<AdminRedemption[]> {
  const res = await api.get('/admin/engagement/redemptions', { params });
  return (res.data as AdminRedemptionsResponse).items || [];
}

export async function adminUpdateRedemptionStatus(id: number, action: 'approve'|'reject'|'fulfill'): Promise<{ ok: boolean; status: string }>{
  const res = await api.patch(`/admin/engagement/redemptions/${id}`, { action });
  return res.data as { ok: boolean; status: string };
}

export async function adminGetLeaderboard(metric: 'points'|'level'|'xp' = 'points', limit?: number): Promise<LeaderboardResponse> {
  const res = await api.get('/admin/engagement/leaderboard', { params: { metric, limit } });
  return res.data as LeaderboardResponse;
}

export type AdminPointsTx = {
  id: number;
  user_id: number;
  delta: number;
  xp_delta?: number;
  reason: string;
  meta?: Record<string, any>;
  created_at: string;
};

export async function adminListTransactions(params?: { user_id?: number|string; reason?: string; limit?: number }): Promise<AdminPointsTx[]> {
  const res = await api.get('/admin/engagement/transactions', { params });
  return (res.data?.items || []) as AdminPointsTx[];
}

export async function adminAdjustPoints(body: { user_id: number; delta: number; reason?: string; meta?: Record<string, any>; current_password?: string }): Promise<{ ok: boolean }>{
  const res = await api.post('/admin/engagement/adjust', body);
  return res.data as { ok: boolean };
}

export type AdminUserProfile = {
  user_id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  points: number;
  level: number;
  xp: number;
  username?: string;
  avatar_url?: string;
  animated_avatar_url?: string;
  avatar_upload_unlocked?: boolean;
};

export async function adminGetUserProfile(user_id: number | string): Promise<AdminUserProfile> {
  const res = await api.get(`/admin/engagement/profile/${user_id}`);
  return res.data as AdminUserProfile;
}

export type AdminUserListItem = {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
};

export async function adminListUsers(): Promise<AdminUserListItem[]> {
  const res = await api.get('/admin/users');
  return (res.data || []) as AdminUserListItem[];
}
